import { ImageName } from "@mcleod/images";
import { BlurEvent, Button, Checkbox, DataSourceMode, DropdownItem, Image, Label, Layout, SlideoutDecorator, TableRow, Textbox, Toast, ValidationResult } from "@mcleod/components";
import { Api, DatePart, DateUtil, ModelRow, PermissionsUtil, StringUtil } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";import { AutogenLayoutYSplitRecurringOrder } from "./autogen/AutogenLayoutYSplitRecurringOrder";
import { RecStopEntry } from "./RecStopEntry";
import { YSplitStopsTableRecurringOrder } from "./YSplitStopsTableRecurringOrder";

interface YSplitSlideoutPropsForRecurringOrder {
    recurOrderId: string;
    isCompanyBoth: boolean;
    stopIds?: string[];
    movementSequence?: string;
    leaveFreight?: boolean;
    crossDock?: boolean;
    doAfterSlideOut?: (success: boolean) => void;
}

// This information from the recurring order is used to determine whether the order can be split
interface YSplitValidationDataForRecurringOrder {
    brokerage: boolean;
    ltl: boolean;
}

const actionButtonProps = {
    caption: "Save",
    backgroundColor: "primary",
    color: "primary.reverse",
    busyWhenDataSourceBusy: true,
    paddingTop: 10,
    paddingBottom: 10,
    rowBreak: false,
    borderWidth: 0,
    minWidth: 128
}

const STOP_DELIVERED: string = "D";

export class YSplitRecurringOrder extends AutogenLayoutYSplitRecurringOrder {
    public actionButton: Button;
    private _stopType: string = "SD";
    private _success: boolean = false;
    public dateEdited: boolean = false;
    private orderId: string;
    public static readonly PICKUP_STOP_TYPES: string[] = ["PU"];
    public static readonly DELIVERY_STOP_TYPES: string[] = ["SO", "SD", "AD"];

    public static validateYSplitForRecurringOrder(data: YSplitValidationDataForRecurringOrder): boolean {
        if (data == null) {
            return false;
        }

        if (data.ltl) {
            CommonDialogs.showDialog("Cannot split LTL movements.");
            return false;
        }

        return true;
    }

    sourceStopAfterModeChange() {
        this.activeRow.set("stop_type", this.stopType);
    }

    public static showSlideoutForRecurringOrder(props: YSplitSlideoutPropsForRecurringOrder) {
        const layout = Layout.getLayout("lme/dispatch/YSplitRecurringOrder") as YSplitRecurringOrder;
        const actionButton = new Button({...actionButtonProps});
        const title = "Create Y-Split - Order ";

        new SlideoutDecorator({
            layout,
            width: '90%',
            fillHeight: true,
            title: title,
            displayOverlay: true,
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            addlComponents: [actionButton],
            layoutLoadListeners: () => {
                layout.orderId = props.recurOrderId;
                layout.mainDataSource.mode = DataSourceMode.ADD;
                layout.setupActionButtonForRecurringOrder(actionButton, props);
                layout.setupStopEntry(layout.layoutRecStopEntry);
                layout.setupYSplitStopsTableRecurringOrder(layout.layoutYSplitStopsTableRecurringOrder, props);
            },
            doAfterSlideOut: () => {
                props.doAfterSlideOut(layout.success);
            },
        });
    }

    onLoad(): void | Promise<void> {
        this.layoutRecStopEntry.panelAdditionalDetails.visible = false;
    }

    async setupYSplitStopsTableRecurringOrder(layout: YSplitStopsTableRecurringOrder, props: YSplitSlideoutPropsForRecurringOrder) {
        const searchParam =  {"order_id": props.recurOrderId};
        await layout.sourceStopRecOrder.search(searchParam);
        layout.orderId = props.recurOrderId;
        for (const modelRow of layout.tableRecOrderStops.data) {
            const stopType = modelRow.get("stop_type");
            const tableRow = layout.tableRecOrderStops.findTableRow(modelRow);
            await tableRow.populateDOMIfNeeded();
            if ( YSplitRecurringOrder.DELIVERY_STOP_TYPES.includes(stopType)) {
                layout.selectStop(tableRow);
                break;
            }
        }
    }

    setupActionButtonForRecurringOrder(actionButton: Button, props: YSplitSlideoutPropsForRecurringOrder) {
        this.actionButton = actionButton;
        this.actionButton.dataSource = this.mainDataSource;
        this.actionButton.addClickListener(() => {
            this.createYSplit();
        });
    }

    setupStopEntry(layout: RecStopEntry) {
        layout.textboxStopType.color = "primary";
        layout.textboxStopType.fontSize = "xlarge";
        layout.textboxStopType.printable = true;
        layout.textboxContactName.lookupModelLayoutHeight = 200;
        layout.newEntry().then(() => {
            layout.activeRow.set("stop_type", "SD");
            layout.textboxStopType.selectedItem = new DropdownItem("SD", "Split Drop");
        });
    }

    async createYSplit() {
        const data = this.mainDataSource.data as ModelRow[];
        if (data) {
            this.actionButton.busy = true;
            try {
                if (this.layoutYSplitStopsTableRecurringOrder.selectedStop == null) {
                    if (!this.mainDataSource.validateSimple()) {
                        CommonDialogs.showError("To save changes, a Y-Split stop must be selected and filled with the necessary information.");
                    } else {
                        CommonDialogs.showDialog([
                            new Label({ text: "Please select a", rowBreak: false }), new Image({ name: ImageName.add, color: "success", rowBreak: false }),
                            new Label({ text: "from the stops list."})
                        ], { title: "Error" });
                    }
                    return;
                }

                if (this.mainDataSource.validate()) {
                    const existingRows: ModelRow[] = this.getExistingRows();
                    if (existingRows.some((modelRow: ModelRow) => modelRow.hasChanged())) {
                        try {
                            const response = await Api.post("lme/dispatch/asset/reorganize-stops",
                                {
                                    "order_id": this.orderId,
                                    "stops": existingRows
                                });
                            if (!response?.data?.[0]?.success) {
                                CommonDialogs.showError("Unexpected problem updating existing stops.");
                                return;
                            }
                        } catch (e) {
                            CommonDialogs.showError(e);
                            return;
                        }
                    }

                    const targetStop: ModelRow = this.layoutYSplitStopsTableRecurringOrder.selectedStop;
                    this.layoutRecStopEntry.layoutStopNotesAndRefNbrs.saveChangesToRow(data[0]);
                    const response = await Api.post("lme/dispatch/create-ysplit-for-recurring", {
                        "new_stop": data,
                        "target_stop_id": targetStop.get("id")
                    }).finally(() => this.actionButton.busy = false);
                    this.success = response.data[0]?.success ?? false;
                    if (!this.success) {
                        const message = response.data[0]?.message ?? "Could not create Y-split";
                        CommonDialogs.showError(message, "Error creating Y-split");
                    } else {
                        this.slideOut();
                    }
                }
            } finally {
                this.actionButton.busy = false;
            }
        }
    }

    getExistingRows() {
        return this.layoutYSplitStopsTableRecurringOrder.tableRecOrderStops.data.filter((modelRow: ModelRow) => modelRow._appending == false);
    }

    public get stopType(): string {
        return this._stopType;
    }

    public set stopType(value: string) {
        this._stopType = value;
    }

    public get success(): boolean {
        return this._success;
    }

    public set success(value: boolean) {
        this._success = value;
    }
}
